import { Injectable } from '@angular/core';
import {
  Subscription,
  SubscriptionTier,
  SubscriptionAddon,
  UsageStatistics,
  TrialStatus,
  PlanChangePreview,
  PendingPlanChange,
} from '@core/models/subscription.model';
import { Store, StoreConfig } from '@datorama/akita';

export interface SubscriptionState {
  currentSubscription: Subscription | null;
  trialStatus: TrialStatus | null;
  planChangePreview: PlanChangePreview | null;
  pendingPlanChange: PendingPlanChange | null;
  availableTiers: {
    gp: SubscriptionTier[];
    lp: SubscriptionTier[];
    all: SubscriptionTier[];
  };
  availableAddons: SubscriptionAddon[];
  usageStatistics: {
    [key: string]: UsageStatistics;
  };
  loading: {
    currentSubscription: boolean;
    trialStatus: boolean;
    planChange: boolean;
    tiers: boolean;
    addons: boolean;
    usage: boolean;
  };
  errors: {
    currentSubscription: string | null;
    trialStatus: string | null;
    planChange: string | null;
    tiers: string | null;
    addons: string | null;
    usage: string | null;
  };
}

function createInitialState(): SubscriptionState {
  return {
    currentSubscription: null,
    trialStatus: null,
    planChangePreview: null,
    pendingPlanChange: null,
    availableTiers: {
      gp: [],
      lp: [],
      all: [],
    },
    availableAddons: [],
    usageStatistics: {},
    loading: {
      currentSubscription: false,
      trialStatus: false,
      planChange: false,
      tiers: false,
      addons: false,
      usage: false,
    },
    errors: {
      currentSubscription: null,
      trialStatus: null,
      planChange: null,
      tiers: null,
      addons: null,
      usage: null,
    },
  };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'subscriptions', resettable: true })
export class SubscriptionStore extends Store<SubscriptionState> {
  constructor() {
    super(createInitialState());
  }

  setCurrentSubscription(subscription: Subscription | null): void {
    this.update({
      currentSubscription: subscription,
      loading: { ...this.getValue().loading, currentSubscription: false },
      errors: { ...this.getValue().errors, currentSubscription: null },
    });
  }

  setCurrentSubscriptionLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, currentSubscription: loading },
    });
  }

  setCurrentSubscriptionError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, currentSubscription: error },
      loading: { ...this.getValue().loading, currentSubscription: false },
    });
  }

  setAvailableTiers(tiers: SubscriptionTier[], tierType?: 'gp' | 'lp' | 'all'): void {
    const currentState = this.getValue();
    const updatedTiers = { ...currentState.availableTiers };

    if (tierType === 'gp') {
      updatedTiers.gp = tiers;
    } else if (tierType === 'lp') {
      updatedTiers.lp = tiers;
    } else {
      updatedTiers.all = tiers;
    }

    this.update({
      availableTiers: updatedTiers,
      loading: { ...currentState.loading, tiers: false },
      errors: { ...currentState.errors, tiers: null },
    });
  }

  setTiersLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, tiers: loading },
    });
  }

  setTiersError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, tiers: error },
      loading: { ...this.getValue().loading, tiers: false },
    });
  }

  setAvailableAddons(addons: SubscriptionAddon[]): void {
    this.update({
      availableAddons: addons,
      loading: { ...this.getValue().loading, addons: false },
      errors: { ...this.getValue().errors, addons: null },
    });
  }

  setAddonsLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, addons: loading },
    });
  }

  setAddonsError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, addons: error },
      loading: { ...this.getValue().loading, addons: false },
    });
  }

  setUsageStatistics(usage: UsageStatistics | null, month: number, year: number): void {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const currentState = this.getValue();
    const updatedUsage = { ...currentState.usageStatistics };

    if (usage) {
      updatedUsage[key] = usage;
    } else {
      delete updatedUsage[key];
    }

    this.update({
      usageStatistics: updatedUsage,
      loading: { ...currentState.loading, usage: false },
      errors: { ...currentState.errors, usage: null },
    });
  }

  setUsageLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, usage: loading },
    });
  }

  setUsageError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, usage: error },
      loading: { ...this.getValue().loading, usage: false },
    });
  }

  setTrialStatus(trialStatus: TrialStatus | null): void {
    this.update({
      trialStatus,
      loading: { ...this.getValue().loading, trialStatus: false },
      errors: { ...this.getValue().errors, trialStatus: null },
    });
  }

  setTrialStatusLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, trialStatus: loading },
    });
  }

  setTrialStatusError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, trialStatus: error },
      loading: { ...this.getValue().loading, trialStatus: false },
    });
  }

  setPlanChangePreview(preview: PlanChangePreview | null): void {
    this.update({
      planChangePreview: preview,
      loading: { ...this.getValue().loading, planChange: false },
      errors: { ...this.getValue().errors, planChange: null },
    });
  }

  setPendingPlanChange(pendingPlanChange: PendingPlanChange | null): void {
    this.update({ pendingPlanChange });
    const current = this.getValue().currentSubscription;
    if (current) {
      this.update({
        currentSubscription: {
          ...current,
          pendingPlanChange,
        },
      });
    }
  }

  setPlanChangeLoading(loading: boolean): void {
    this.update({
      loading: { ...this.getValue().loading, planChange: loading },
    });
  }

  setPlanChangeError(error: string | null): void {
    this.update({
      errors: { ...this.getValue().errors, planChange: error },
      loading: { ...this.getValue().loading, planChange: false },
    });
  }
}
